import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootStore } from '../../../store/types';
import { TitleSearchPage } from './TitleSearchPage';
import { cancelTitleSearch, createTitleSearch } from '../../../store/order/titleSearchRequests/dispatch';
import { cancelPayoff, createPayoff, getPayoffRequest } from '../../../store/order/payoffRequests/dispatch';
import { useSelectCurrentOrder } from 'selectors/order/selectCurrentOrder';
import { cancelTaxCertification, createTaxCertification } from '../../../store/order/taxCertifications/dispatch';
import { ePartyPurpose } from '../../../ui-toolkit/enums/ePartyPurpose';
import { IPartyEntity } from '../../../ui-toolkit/orders/interfaces/IPartyEntity';
import { useAppDispatch } from '../../../store/store';

interface ITitleTitleSearchContainerProps {
  orderId: string;
}

export const TitleSearchContainer: React.FC<ITitleTitleSearchContainerProps> = ({ orderId }) => {
  const dispatch = useAppDispatch();
  const {
    titleSearchRequest,
    titleSearchRequestFetchStatus,
    redVisionSearch,
    cancelTitleSearchRequestError,
    createTitleSearchRequestError,
    cancelTitleSearchRequestFetchStatus,
    createTitleSearchRequestFetchStatus,
  } = useSelector(({ order }: RootStore) => order.titleSearchRequests);
  const {
    payoffRequest,
    cancelPayoffRequestError,
    cancelPayoffRequestFetchStatus,
    createPayoffRequestError,
    createPayoffRequestFetchStatus,
  } = useSelector(({ order }: RootStore) => order.payoffRequests);

  const {
    taxCertificationRequestsSlice: { data: taxCertRequestData },
    cancelTaxCertificationSlice: { fetchStatus: cancelTaxCertFetchStatus, fetchError: cancelTaxCertFetchError },
    createTaxCertificationRequestSlice: { fetchStatus: createTaxCertFetchStatus, fetchError: createTaxCertFetchError },
  } = useSelector(
    ({
      order: {
        taxCertification: {
          taxCertificationRequestsSlice,
          cancelTaxCertificationSlice,
          createTaxCertificationRequestSlice,
        },
      },
    }: RootStore) => ({
      taxCertificationRequestsSlice,
      cancelTaxCertificationSlice,
      createTaxCertificationRequestSlice,
    })
  );

  const { data } = useSelectCurrentOrder();
  useEffect(() => {
    dispatch(getPayoffRequest(orderId));
  }, [orderId, dispatch]);

  const onCreateNewTitleSearch = () => dispatch(createTitleSearch(orderId));
  const onCancelTitleSearch = (titleSearchId: string) => dispatch(cancelTitleSearch(orderId, titleSearchId));

  const renderTitleIssuedAgentButton = (): boolean => {
    const titleIssuedAgent = data.parties.find((p) => p.purpose === ePartyPurpose.titleIssuingAgent);
    const lender = data.lender;
    return (
      !!titleIssuedAgent &&
      !!(titleIssuedAgent as IPartyEntity).entity_name &&
      !(titleIssuedAgent as IPartyEntity).entity_name.includes('Spruce') &&
      !!lender &&
      !!lender.name &&
      !['Point', 'Patch Homes', 'Unison'].includes(lender.name)
    );
  };

  const onCreatePayoff = () => dispatch(createPayoff(orderId));
  const onCancelPayoff = (payoffId: string) => dispatch(cancelPayoff(orderId, payoffId));

  const onCreateTaxCertification = () => dispatch(createTaxCertification(orderId));
  const onCancelTaxCertification = (taxCertificationRequestId: string) =>
    dispatch(cancelTaxCertification(orderId, taxCertificationRequestId));

  return (
    <TitleSearchPage
      titleSearchRequests={titleSearchRequest[orderId] || []}
      titleSearchRequestFetchStatus={titleSearchRequestFetchStatus}
      onCreateNewTitleSearch={onCreateNewTitleSearch}
      onCancelTitleSearch={onCancelTitleSearch}
      orderId={orderId}
      redVisionSearch={redVisionSearch[orderId]}
      renderTitleIssuedAgentButton={renderTitleIssuedAgentButton()}
      onCancelFetchStatus={cancelTitleSearchRequestFetchStatus}
      onCreateFetchStatus={createTitleSearchRequestFetchStatus}
      onCreateError={createTitleSearchRequestError}
      onCancelError={cancelTitleSearchRequestError}
      payoffs={payoffRequest[orderId] || []}
      createPayoffError={createPayoffRequestError}
      createFetchStatus={createPayoffRequestFetchStatus}
      cancelPayoffError={cancelPayoffRequestError}
      cancelFetchStatus={cancelPayoffRequestFetchStatus}
      onCreatePayoff={onCreatePayoff}
      onCancelPayoff={onCancelPayoff}
      taxCertificationRequest={taxCertRequestData}
      onCancelTaxCertification={onCancelTaxCertification}
      onCreateTaxCertification={onCreateTaxCertification}
      createTaxCertFetchStatus={createTaxCertFetchStatus}
      cancelTaxCertFetchStatus={cancelTaxCertFetchStatus}
      cancelTaxCertificationError={cancelTaxCertFetchError}
      createTaxCertificationRequestError={createTaxCertFetchError}
    />
  );
};
